/**
 * Universal Logger Factory System
 * 
 * Factory pattern implementation for creating namespace-specific loggers
 * with pre-configured context, metadata, and specialized behavior.
 */

import { 
  UniversalLogger,
  getUniversalLogger
} from './universal-logger';

import {
  IUniversalLogger,
  LogNamespace,
  MemoryUsage,
  PerformanceMetrics
} from './types';

import {
  EnhancedContextProvider,
  ContextProviderManager,
  globalContextProviderManager
} from './context-provider-integration';

/**
 * Enhanced namespace logger interface with factory-specific features
 */
export interface INamespaceLogger extends IUniversalLogger {
  readonly namespace: LogNamespace;
  readonly factoryMetadata: NamespaceFactoryMetadata;
  
  // Enhanced logging methods with automatic context injection
  errorWithContext: (message: string, details?: Record<string, any>, error?: Error) => void;
  warnWithContext: (message: string, details?: Record<string, any>) => void;
  infoWithContext: (message: string, details?: Record<string, any>) => void;
  debugWithContext: (message: string, details?: Record<string, any>) => void;
  
  // Namespace-specific operation tracking
  startNamespaceOperation: (operationName: string, details?: Record<string, any>) => string;
  
  // Context management
  updateContext: (context: Partial<NamespaceContext>) => void;
  getContext: () => NamespaceContext;
  clearContext: () => void;
}

/**
 * Factory metadata for namespace loggers
 */
export interface NamespaceFactoryMetadata {
  namespace: LogNamespace;
  createdAt: string;
  version: string;
  defaultMetadata: Record<string, any>;
  contextProviders: ContextProvider[];
  configuration: NamespaceConfiguration;
}

/**
 * Namespace-specific context
 */
export interface NamespaceContext {
  userId?: string;
  sessionId?: string;
  requestId?: string;
  operationId?: string;
  customMetadata: Record<string, any>;
}

/**
 * Context provider for injecting namespace-specific metadata
 */
export interface ContextProvider {
  name: string;
  priority: number;
  enabled: boolean;
  provide: (context: NamespaceContext) => Record<string, any> | Promise<Record<string, any>>;
}

/**
 * Namespace-specific configuration
 */
export interface NamespaceConfiguration {
  defaultLogLevel?: string;
  enablePerformanceTracking?: boolean;
  enableSensitiveDataFiltering?: boolean;
  customFilterRules?: string[];
  contextInjection: {
    enabled: boolean;
    includeUserId: boolean;
    includeSessionId: boolean;
    includeRequestId: boolean;
    includeTimestamp: boolean;
    customFields: string[];
  };
  performanceThresholds?: {
    operationWarningMs?: number;
    operationErrorMs?: number;
    memoryWarningMB?: number;
    memoryErrorMB?: number;
  };
}

/**
 * Factory configuration for namespace loggers
 */
export interface LoggerFactoryConfig {
  // Global factory settings
  enableCaching: boolean;
  cacheTimeout: number;
  enablePerformanceMonitoring: boolean;
  enableContextProviders: boolean;
  
  // Default namespace configurations
  defaultConfiguration: NamespaceConfiguration;
  
  // Namespace-specific overrides
  namespaceConfigurations: Map<LogNamespace, NamespaceConfiguration>;
  
  // Context providers registry
  globalContextProviders: ContextProvider[];
  namespaceContextProviders: Map<LogNamespace, ContextProvider[]>;
}

/**
 * Logger factory interface
 */
export interface ILoggerFactory {
  /**
   * Create or retrieve a namespace-specific logger
   */
  createLogger(namespace: LogNamespace, context?: Partial<NamespaceContext>): INamespaceLogger;
  
  /**
   * Register a context provider for a specific namespace
   */
  registerContextProvider(namespace: LogNamespace, provider: ContextProvider): void;
  
  /**
   * Register a global context provider (applies to all namespaces)
   */
  registerGlobalContextProvider(provider: ContextProvider): void;
  
  /**
   * Update configuration for a specific namespace
   */
  updateNamespaceConfiguration(namespace: LogNamespace, config: Partial<NamespaceConfiguration>): void;
  
  /**
   * Get all cached loggers
   */
  getCachedLoggers(): Map<LogNamespace, INamespaceLogger>;
  
  /**
   * Clear cache and force recreation of loggers
   */
  clearCache(): void;
  
  /**
   * Get factory statistics
   */
  getFactoryStats(): LoggerFactoryStats;

  /**
   * Register an enhanced context provider for a specific namespace
   */
  registerEnhancedContextProvider?(namespace: LogNamespace, provider: EnhancedContextProvider): void;

  /**
   * Get context provider manager statistics
   */
  getContextProviderStats?(): any;

  /**
   * Dispose of a specific logger and its resources
   */
  disposeLogger?(namespace: LogNamespace): boolean;

  /**
   * Dispose of all loggers and clean up resources
   */
  disposeAll?(): void;

  /**
   * Check if a logger is cached and active
   */
  isLoggerActive?(namespace: LogNamespace): boolean;

  /**
   * Get logger instance information
   */
  getLoggerInfo?(namespace: LogNamespace): NamespaceLoggerInfo | null;

  /**
   * Refresh a cached logger (force recreation)
   */
  refreshLogger?(namespace: LogNamespace): INamespaceLogger;

  /**
   * Pre-warm loggers for specified namespaces
   */
  preWarmLoggers?(namespaces: LogNamespace[]): Promise<void>;
}

/**
 * Factory statistics interface
 */
export interface LoggerFactoryStats {
  totalLoggersCreated: number;
  cachedLoggers: number;
  activeNamespaces: LogNamespace[];
  contextProvidersCount: number;
  performanceMetrics: {
    averageCreationTime: number;
    cacheHitRate: number;
    contextProviderExecutionTime: number;
  };
}

/**
 * Logger instance information interface
 */
export interface NamespaceLoggerInfo {
  namespace: LogNamespace;
  createdAt: string;
  lastAccessed: string;
  accessCount: number;
  isActive: boolean;
  cacheValid: boolean;
  memoryUsage: number;
  configuration: NamespaceConfiguration;
  contextProviders: ContextProvider[];
}

/**
 * Concrete implementation of the Logger Factory
 */
export class UniversalLoggerFactory implements ILoggerFactory {
  private static instance: UniversalLoggerFactory | null = null;
  
  private config: LoggerFactoryConfig;
  private loggerCache: Map<LogNamespace, INamespaceLogger> = new Map();
  private cacheTimestamps: Map<LogNamespace, number> = new Map();
  private stats: LoggerFactoryStats;
  
  // Enhanced context provider manager
  private contextProviderManager: ContextProviderManager;
  
  // Logger lifecycle management
  private loggerAccessCounts: Map<LogNamespace, number> = new Map();
  private loggerLastAccessed: Map<LogNamespace, number> = new Map();
  private loggerCreationTimes: Map<LogNamespace, number> = new Map();
  private disposedLoggers: Set<LogNamespace> = new Set();
  
  // Thread safety lock for logger operations
  private loggerOperationLock: Map<LogNamespace, Promise<void>> = new Map();
  
  // Performance tracking
  private creationTimes: number[] = [];
  private cacheHits = 0;
  private cacheMisses = 0;
  private contextProviderTimes: number[] = [];
  
  private constructor(config?: Partial<LoggerFactoryConfig>) {
    this.config = this.createDefaultConfig(config);
    this.contextProviderManager = new ContextProviderManager();
    this.stats = {
      totalLoggersCreated: 0,
      cachedLoggers: 0,
      activeNamespaces: [],
      contextProvidersCount: 0,
      performanceMetrics: {
        averageCreationTime: 0,
        cacheHitRate: 0,
        contextProviderExecutionTime: 0
      }
    };
  }
  
  /**
   * Get singleton instance of the factory
   */
  static getInstance(config?: Partial<LoggerFactoryConfig>): UniversalLoggerFactory {
    if (!UniversalLoggerFactory.instance) {
      UniversalLoggerFactory.instance = new UniversalLoggerFactory(config);
    }
    return UniversalLoggerFactory.instance;
  }
  
  /**
   * Reset the singleton instance (primarily for testing)
   */
  static resetInstance(): void {
    UniversalLoggerFactory.instance = null;
  }
  
  /**
   * Create default factory configuration
   */
  private createDefaultConfig(userConfig?: Partial<LoggerFactoryConfig>): LoggerFactoryConfig {
    const defaultConfig: LoggerFactoryConfig = {
      enableCaching: true,
      cacheTimeout: 300000, // 5 minutes
      enablePerformanceMonitoring: true,
      enableContextProviders: true,
      
      defaultConfiguration: {
        enablePerformanceTracking: true,
        enableSensitiveDataFiltering: true,
        contextInjection: {
          enabled: true,
          includeUserId: true,
          includeSessionId: true,
          includeRequestId: true,
          includeTimestamp: true,
          customFields: []
        },
        performanceThresholds: {
          operationWarningMs: 1000,
          operationErrorMs: 5000,
          memoryWarningMB: 100,
          memoryErrorMB: 500
        }
      },
      
      namespaceConfigurations: new Map(),
      globalContextProviders: [],
      namespaceContextProviders: new Map()
    };
    
    return { ...defaultConfig, ...userConfig };
  }
  
  /**
   * Create or retrieve a namespace-specific logger
   */
  createLogger(namespace: LogNamespace, context?: Partial<NamespaceContext>): INamespaceLogger {
    // Check if logger was disposed and clean up
    if (this.disposedLoggers.has(namespace)) {
      this.disposedLoggers.delete(namespace);
      this.clearNamespaceCache(namespace);
    }
    
    const startTime = performance.now();
    
    // Check cache first
    if (this.config.enableCaching && this.isCacheValid(namespace)) {
      this.cacheHits++;
      const cachedLogger = this.loggerCache.get(namespace)!;
      
      // Track access
      this.trackLoggerAccess(namespace);
      
      // Update context if provided
      if (context) {
        cachedLogger.updateContext(context);
      }
      
      return cachedLogger;
    }
    
    this.cacheMisses++;
    
    // Create new logger
    const logger = this.createNamespaceLogger(namespace, context);
    
    // Cache the logger and track lifecycle
    if (this.config.enableCaching) {
      this.cacheLogger(namespace, logger);
    }
    
    this.trackLoggerCreation(namespace);
    this.trackLoggerAccess(namespace);
    
    // Update statistics
    const creationTime = performance.now() - startTime;
    this.creationTimes.push(creationTime);
    this.stats.totalLoggersCreated++;
    this.updateStats();
    
    return logger;
  }
  
  /**
   * Check if cached logger is still valid
   */
  private isCacheValid(namespace: LogNamespace): boolean {
    if (!this.loggerCache.has(namespace)) return false;
    
    const cacheTime = this.cacheTimestamps.get(namespace);
    if (!cacheTime) return false;
    
    return Date.now() - cacheTime < this.config.cacheTimeout;
  }
  
  /**
   * Create a new namespace logger with enhanced features
   */
  private createNamespaceLogger(namespace: LogNamespace, initialContext?: Partial<NamespaceContext>): INamespaceLogger {
    const universalLogger = getUniversalLogger();
    const baseLogger = universalLogger.createNamespaceLogger(namespace);
    
    // Get namespace configuration
    const config = this.getNamespaceConfiguration(namespace);
    
    // Create factory metadata
    const factoryMetadata: NamespaceFactoryMetadata = {
      namespace,
      createdAt: new Date().toISOString(),
      version: '1.0.0',
      defaultMetadata: this.createDefaultMetadata(namespace),
      contextProviders: this.getContextProviders(namespace),
      configuration: config
    };
    
    // Initialize context
    const namespaceContext: NamespaceContext = {
      customMetadata: {},
      ...initialContext
    };
    
    // Create enhanced namespace logger
    const enhancedLogger: INamespaceLogger = {
      ...baseLogger,
      namespace,
      factoryMetadata,
      
      // Enhanced logging methods with automatic context injection
      errorWithContext: (message: string, details?: Record<string, any>, error?: Error) => {
        const enhancedDetails = this.injectContext(namespace, namespaceContext, details || {});
        baseLogger.error(message, enhancedDetails, undefined, error);
      },
      
      warnWithContext: (message: string, details?: Record<string, any>) => {
        const enhancedDetails = this.injectContext(namespace, namespaceContext, details || {});
        baseLogger.warn(message, enhancedDetails);
      },
      
      infoWithContext: (message: string, details?: Record<string, any>) => {
        const enhancedDetails = this.injectContext(namespace, namespaceContext, details || {});
        baseLogger.info(message, enhancedDetails);
      },
      
      debugWithContext: (message: string, details?: Record<string, any>) => {
        const enhancedDetails = this.injectContext(namespace, namespaceContext, details || {});
        baseLogger.debug(message, enhancedDetails);
      },
      
      // Namespace-specific operation tracking
      startNamespaceOperation: (operationName: string, details?: Record<string, any>) => {
        const enhancedDetails = this.injectContext(namespace, namespaceContext, details || {});
        return baseLogger.startOperation(`${namespace}:${operationName}`, enhancedDetails);
      },
      
      // Context management
      updateContext: (context: Partial<NamespaceContext>) => {
        Object.assign(namespaceContext, context);
      },
      
      getContext: () => ({ ...namespaceContext }),
      
      clearContext: () => {
        namespaceContext.userId = undefined;
        namespaceContext.sessionId = undefined;
        namespaceContext.requestId = undefined;
        namespaceContext.operationId = undefined;
        namespaceContext.customMetadata = {};
      }
    };
    
    return enhancedLogger;
  }
  
  /**
   * Get namespace-specific configuration
   */
  private getNamespaceConfiguration(namespace: LogNamespace): NamespaceConfiguration {
    return this.config.namespaceConfigurations.get(namespace) || this.config.defaultConfiguration;
  }
  
  /**
   * Create default metadata for a namespace
   */
  private createDefaultMetadata(namespace: LogNamespace): Record<string, any> {
    return {
      namespace,
      factoryVersion: '1.0.0',
      createdBy: 'UniversalLoggerFactory'
    };
  }
  
  /**
   * Get context providers for a namespace
   */
  private getContextProviders(namespace: LogNamespace): ContextProvider[] {
    const globalProviders = this.config.globalContextProviders;
    const namespaceProviders = this.config.namespaceContextProviders.get(namespace) || [];
    
    return [...globalProviders, ...namespaceProviders]
      .filter(provider => provider.enabled)
      .sort((a, b) => b.priority - a.priority);
  }
  
  /**
   * Inject context into log details
   */
  private injectContext(
    namespace: LogNamespace, 
    context: NamespaceContext, 
    details: Record<string, any>
  ): Record<string, any> {
    const config = this.getNamespaceConfiguration(namespace);
    
    if (!config.contextInjection.enabled) {
      return details;
    }
    
    const injectedContext: Record<string, any> = {};
    
    // Inject standard context fields
    if (config.contextInjection.includeUserId && context.userId) {
      injectedContext.userId = context.userId;
    }
    
    if (config.contextInjection.includeSessionId && context.sessionId) {
      injectedContext.sessionId = context.sessionId;
    }
    
    if (config.contextInjection.includeRequestId && context.requestId) {
      injectedContext.requestId = context.requestId;
    }
    
    if (config.contextInjection.includeTimestamp) {
      injectedContext.contextTimestamp = new Date().toISOString();
    }
    
    // Inject custom fields
    for (const field of config.contextInjection.customFields) {
      if (context.customMetadata[field] !== undefined) {
        injectedContext[field] = context.customMetadata[field];
      }
    }
    
    // Execute context providers with enhanced capabilities
    if (this.config.enableContextProviders) {
      this.executeContextProviders(namespace, context, injectedContext);
    }
    
    return {
      ...details,
      context: injectedContext
    };
  }

  /**
   * Execute context providers with enhanced features
   */
  private executeContextProviders(
    namespace: LogNamespace, 
    context: NamespaceContext, 
    injectedContext: Record<string, any>
  ): void {
    const providers = this.getContextProviders(namespace);
    
    // Separate enhanced and basic providers
    const enhancedProviders: EnhancedContextProvider[] = [];
    const basicProviders: ContextProvider[] = [];
    
    for (const provider of providers) {
      if (this.isEnhancedProvider(provider)) {
        enhancedProviders.push(provider as EnhancedContextProvider);
      } else {
        basicProviders.push(provider);
      }
    }
    
    // Execute enhanced providers with advanced features
    if (enhancedProviders.length > 0) {
      this.executeEnhancedProviders(enhancedProviders, namespace, context, injectedContext);
    }
    
    // Execute basic providers with legacy approach
    if (basicProviders.length > 0) {
      this.executeBasicProviders(basicProviders, context, injectedContext);
    }
  }

  /**
   * Check if a provider is an enhanced provider
   */
  private isEnhancedProvider(provider: ContextProvider): boolean {
    const enhanced = provider as any;
    return enhanced.timeout !== undefined || 
           enhanced.retries !== undefined || 
           enhanced.conditions !== undefined ||
           enhanced.cacheable !== undefined ||
           enhanced.errorStrategy !== undefined ||
           enhanced.dependencies !== undefined;
  }

  /**
   * Execute enhanced providers using the context provider manager
   */
  private executeEnhancedProviders(
    providers: EnhancedContextProvider[], 
    namespace: LogNamespace, 
    context: NamespaceContext, 
    injectedContext: Record<string, any>
  ): void {
    try {
      // Use async execution with timeout to prevent blocking
      const timeoutPromise = new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 100); // 100ms timeout for logging context
      });
      
      const executionPromise = this.contextProviderManager.executeProviders(providers, context, namespace)
        .then(results => {
          Object.assign(injectedContext, results);
        })
        .catch(error => {
          if (this.config.enablePerformanceMonitoring) {
            console.warn(`Enhanced context providers failed for namespace '${namespace}':`, error);
          }
        });
      
      // Race between execution and timeout
      Promise.race([executionPromise, timeoutPromise]).catch(() => {
        // Silently ignore timeout/errors to not break logging
      });
      
    } catch (error) {
      // Fallback to basic execution if enhanced fails
      if (this.config.enablePerformanceMonitoring) {
        console.warn(`Enhanced provider execution failed, falling back to basic execution:`, error);
      }
      this.executeBasicProviders(providers, context, injectedContext);
    }
  }

  /**
   * Execute basic providers with legacy approach
   */
  private executeBasicProviders(
    providers: ContextProvider[], 
    context: NamespaceContext, 
    injectedContext: Record<string, any>
  ): void {
    for (const provider of providers) {
      try {
        const startTime = performance.now();
        const providerContext = provider.provide(context);
        const providerTime = performance.now() - startTime;
        
        if (this.config.enablePerformanceMonitoring) {
          this.contextProviderTimes.push(providerTime);
        }
        
        // Handle both sync and async providers
        if (providerContext && typeof providerContext === 'object') {
          Object.assign(injectedContext, providerContext);
        }
      } catch (error) {
        // Silently ignore provider errors to not break logging
        if (this.config.enablePerformanceMonitoring) {
          console.warn(`Context provider '${provider.name}' failed:`, error);
        }
      }
    }
  }
  
  /**
   * Register a context provider for a specific namespace
   */
  registerContextProvider(namespace: LogNamespace, provider: ContextProvider): void {
    const providers = this.config.namespaceContextProviders.get(namespace) || [];
    providers.push(provider);
    this.config.namespaceContextProviders.set(namespace, providers);
    this.updateStats();
  }
  
  /**
   * Register a global context provider
   */
  registerGlobalContextProvider(provider: ContextProvider): void {
    this.config.globalContextProviders.push(provider);
    this.updateStats();
  }
  
  /**
   * Update configuration for a specific namespace
   */
  updateNamespaceConfiguration(namespace: LogNamespace, config: Partial<NamespaceConfiguration>): void {
    const currentConfig = this.getNamespaceConfiguration(namespace);
    const updatedConfig = { ...currentConfig, ...config };
    this.config.namespaceConfigurations.set(namespace, updatedConfig);
    
    // Invalidate cache for this namespace
    this.loggerCache.delete(namespace);
    this.cacheTimestamps.delete(namespace);
  }
  
  /**
   * Get all cached loggers
   */
  getCachedLoggers(): Map<LogNamespace, INamespaceLogger> {
    return new Map(this.loggerCache);
  }
  
  /**
   * Clear cache and force recreation of loggers
   */
  clearCache(): void {
    this.loggerCache.clear();
    this.cacheTimestamps.clear();
    this.updateStats();
  }
  
  /**
   * Update internal statistics
   */
  private updateStats(): void {
    this.stats.cachedLoggers = this.loggerCache.size;
    this.stats.activeNamespaces = Array.from(this.loggerCache.keys());
    
    const totalProviders = this.config.globalContextProviders.length + 
      Array.from(this.config.namespaceContextProviders.values())
        .reduce((sum, providers) => sum + providers.length, 0);
    this.stats.contextProvidersCount = totalProviders;
    
    // Calculate performance metrics
    if (this.creationTimes.length > 0) {
      this.stats.performanceMetrics.averageCreationTime = 
        this.creationTimes.reduce((sum, time) => sum + time, 0) / this.creationTimes.length;
    }
    
    const totalRequests = this.cacheHits + this.cacheMisses;
    if (totalRequests > 0) {
      this.stats.performanceMetrics.cacheHitRate = this.cacheHits / totalRequests;
    }
    
    if (this.contextProviderTimes.length > 0) {
      this.stats.performanceMetrics.contextProviderExecutionTime = 
        this.contextProviderTimes.reduce((sum, time) => sum + time, 0) / this.contextProviderTimes.length;
    }
  }
  
  /**
   * Get factory statistics
   */
  getFactoryStats(): LoggerFactoryStats {
    this.updateStats();
    return { ...this.stats };
  }

  /**
   * Register an enhanced context provider for a specific namespace
   */
  registerEnhancedContextProvider(namespace: LogNamespace, provider: EnhancedContextProvider): void {
    const providers = this.config.namespaceContextProviders.get(namespace) || [];
    providers.push(provider);
    this.config.namespaceContextProviders.set(namespace, providers);
    this.updateStats();
  }

  /**
   * Get context provider manager statistics
   */
  getContextProviderStats(): any {
    return {
      providerStats: this.contextProviderManager.getProviderStats(),
      executionHistory: this.contextProviderManager.getExecutionHistory(),
      cacheStats: this.contextProviderManager.getCacheStats()
    };
  }

  /**
   * Dispose of a specific logger and its resources
   */
  disposeLogger(namespace: LogNamespace): boolean {
    if (!this.loggerCache.has(namespace)) {
      return false;
    }

    // Mark as disposed
    this.disposedLoggers.add(namespace);
    
    // Clear from cache
    this.clearNamespaceCache(namespace);
    
    // Update statistics
    this.updateStats();
    
    return true;
  }

  /**
   * Dispose of all loggers and clean up resources
   */
  disposeAll(): void {
    // Mark all cached loggers as disposed
    for (const namespace of this.loggerCache.keys()) {
      this.disposedLoggers.add(namespace);
    }
    
    // Clear all caches
    this.clearCache();
    
    // Clear lifecycle tracking
    this.loggerAccessCounts.clear();
    this.loggerLastAccessed.clear();
    this.loggerCreationTimes.clear();
    this.loggerOperationLock.clear();
    
    // Clear context provider manager
    this.contextProviderManager.clearCache();
    this.contextProviderManager.clearStats();
    
    // Update statistics
    this.updateStats();
  }

  /**
   * Check if a logger is cached and active
   */
  isLoggerActive(namespace: LogNamespace): boolean {
    return this.loggerCache.has(namespace) && 
           !this.disposedLoggers.has(namespace) && 
           this.isCacheValid(namespace);
  }

  /**
   * Get logger instance information
   */
  getLoggerInfo(namespace: LogNamespace): NamespaceLoggerInfo | null {
    if (!this.loggerCache.has(namespace)) {
      return null;
    }

    const logger = this.loggerCache.get(namespace)!;
    const creationTime = this.loggerCreationTimes.get(namespace) || Date.now();
    const lastAccessed = this.loggerLastAccessed.get(namespace) || Date.now();
    const accessCount = this.loggerAccessCounts.get(namespace) || 0;

    return {
      namespace,
      createdAt: new Date(creationTime).toISOString(),
      lastAccessed: new Date(lastAccessed).toISOString(),
      accessCount,
      isActive: this.isLoggerActive(namespace),
      cacheValid: this.isCacheValid(namespace),
      memoryUsage: this.estimateLoggerMemoryUsage(logger),
      configuration: this.getNamespaceConfiguration(namespace),
      contextProviders: this.getContextProviders(namespace)
    };
  }

  /**
   * Refresh a cached logger (force recreation)
   */
  refreshLogger(namespace: LogNamespace): INamespaceLogger {
    // Clear existing cache
    this.clearNamespaceCache(namespace);
    
    // Create new logger
    return this.createLogger(namespace);
  }

  /**
   * Pre-warm loggers for specified namespaces
   */
  async preWarmLoggers(namespaces: LogNamespace[]): Promise<void> {
    const preWarmPromises = namespaces.map(async (namespace) => {
      try {
        this.createLogger(namespace);
      } catch (error) {
        console.warn(`Failed to pre-warm logger for namespace ${namespace}:`, error);
      }
    });

    await Promise.allSettled(preWarmPromises);
  }

  /**
   * Cache a logger with proper lifecycle tracking
   */
  private cacheLogger(namespace: LogNamespace, logger: INamespaceLogger): void {
    this.loggerCache.set(namespace, logger);
    this.cacheTimestamps.set(namespace, Date.now());
  }

  /**
   * Clear namespace-specific cache entries
   */
  private clearNamespaceCache(namespace: LogNamespace): void {
    this.loggerCache.delete(namespace);
    this.cacheTimestamps.delete(namespace);
    this.loggerAccessCounts.delete(namespace);
    this.loggerLastAccessed.delete(namespace);
    this.loggerCreationTimes.delete(namespace);
    this.loggerOperationLock.delete(namespace);
  }

  /**
   * Track logger creation time
   */
  private trackLoggerCreation(namespace: LogNamespace): void {
    this.loggerCreationTimes.set(namespace, Date.now());
  }

  /**
   * Track logger access
   */
  private trackLoggerAccess(namespace: LogNamespace): void {
    const currentCount = this.loggerAccessCounts.get(namespace) || 0;
    this.loggerAccessCounts.set(namespace, currentCount + 1);
    this.loggerLastAccessed.set(namespace, Date.now());
  }

  /**
   * Estimate memory usage of a logger instance
   */
  private estimateLoggerMemoryUsage(logger: INamespaceLogger): number {
    try {
      // Rough estimation based on object serialization
      const serialized = JSON.stringify(logger.factoryMetadata);
      return serialized.length * 2; // Rough estimate in bytes
    } catch {
      return 1024; // Default estimate if serialization fails
    }
  }
}

/**
 * Convenience function to get the logger factory instance
 */
export const getLoggerFactory = (config?: Partial<LoggerFactoryConfig>): UniversalLoggerFactory => 
  UniversalLoggerFactory.getInstance(config);

/**
 * Pre-configured factory methods for common namespaces
 */
export const LoggerFactoryMethods = {
  /**
   * Create an authentication logger
   */
  createAuthLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.AUTH, context),
  
  /**
   * Create an API logger
   */
  createApiLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.API, context),
  
  /**
   * Create a UI logger
   */
  createUiLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.UI, context),
  
  /**
   * Create a performance logger
   */
  createPerformanceLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.PERFORMANCE, context),
  
  /**
   * Create a database logger
   */
  createDatabaseLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.DATABASE, context),
  
  /**
   * Create a classification logger
   */
  createClassificationLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.CLASSIFICATION, context),
  
  /**
   * Create a judge logger
   */
  createJudgeLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.JUDGE, context),
  
  /**
   * Create a storage logger
   */
  createStorageLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.STORAGE, context),
  
  /**
   * Create a worker logger
   */
  createWorkerLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.WORKER, context),
  
  /**
   * Create a metrics logger
   */
  createMetricsLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.METRICS, context),
  
  /**
   * Create a security logger
   */
  createSecurityLogger: (context?: Partial<NamespaceContext>) => 
    getLoggerFactory().createLogger(LogNamespace.SECURITY, context)
}; 